import { publicImageHost } from "src/utils/imageUrl.js";
import { LOGO_FILES } from "./logoRails.js";

/** nav slug → hosted logo slug, only where the two differ. */
const LOGO_ALIAS = {
  hbo: "max",
  disney: "disney-plus",
  paramount: "paramount-plus",
  "history-channel": "history",
};

/** All D1 conference slugs render a shield rather than a per-league symbol. */
const CONFERENCES = new Set([
  "sec", "big-10", "acc", "big-12", "big-east", "aac", "america-east", "atlantic-10",
  "atlantic-sun", "big-sky", "big-south", "big-west", "coastal-athletic", "horizon", "ivy",
  "maac", "mac", "meac", "missouri-valley", "mountain-west", "northeast", "ohio-valley",
  "patriot-league", "southern", "southland", "summit-league", "sunbelt", "swac", "wac", "west-coast",
]);

/** Material Symbol for nodes that have no hosted brand logo. */
const SLUG_SYMBOL = {
  // sports — containers + leagues without a hosted logo
  soccer: "sports_soccer",
  "ligue-1": "sports_soccer", eredivisie: "sports_soccer", "efl-championship": "sports_soccer",
  "efl-league-one": "sports_soccer", "uefa-europa-league": "sports_soccer",
  "uefa-conference-league": "sports_soccer", "uefa-nations-league": "sports_soccer",
  "fa-cup": "sports_soccer", "coppa-italia": "sports_soccer", "concacaf-gold-cup": "sports_soccer",
  ncaa: "school", "power-conferences": "military_tech", "more-conferences": "school",
  "college-football": "sports_football", "college-basketball": "sports_basketball",
  "march-madness": "emoji_events", "college-baseball": "sports_baseball",
  // other college sports
  "other-college-sports": "sports", "college-soccer": "sports_soccer",
  "college-golf": "sports_golf", "college-tennis": "sports_tennis",
  "college-volleyball": "sports_volleyball", "college-beach-volleyball": "sports_volleyball",
  "college-cross-country": "sprint", "college-track-and-field": "sprint",
  "college-swimming-and-diving": "pool", "college-water-polo": "pool",
  "college-softball": "sports_baseball", "college-lacrosse": "sports",
  "college-rowing": "rowing", "college-ice-hockey": "sports_hockey",
  "college-field-hockey": "sports_hockey", "college-wrestling": "sports_martial_arts",
  "college-gymnastics": "sports_gymnastics", "college-fencing": "sports_martial_arts",
  "college-sailing": "sailing", "college-rugby": "sports_rugby",
  "college-skiing": "downhill_skiing", "college-squash": "sports_tennis",
  "college-triathlon": "sprint", "college-bowling": "sports",
  "college-rifle": "sports", "college-equestrian": "sports",
  "minor-leagues": "sports", milb: "sports_baseball", usl: "sports_soccer",
  ahl: "sports_hockey", "g-league": "sports_basketball", echl: "sports_hockey",
  tennis: "sports_tennis", "australian-open": "sports_tennis", "french-open": "sports_tennis",
  wimbledon: "sports_tennis", "us-open": "sports_tennis",
  golf: "sports_golf", pga: "sports_golf", lpga: "sports_golf", liv: "sports_golf",
  "champions-tour": "sports_golf",
  racing: "sports_motorsports", "formula-1": "sports_motorsports", nascar: "sports_motorsports",
  "womens-sports": "sports", wnba: "sports_basketball", nwsl: "sports_soccer", pwhl: "sports_hockey",
  rugby: "sports_rugby", "rugby-league": "sports_rugby", "nrl-premiership": "sports_rugby",
  "super-league": "sports_rugby", "state-of-origin": "sports_rugby", "rfl-championship": "sports_rugby",
  "rfl-league-1": "sports_rugby",
  cricket: "sports_cricket", ipl: "sports_cricket", bbl: "sports_cricket", hundred: "sports_cricket",
  // tv — group headers + streamers without a hosted logo
  streaming: "subscriptions", networks: "live_tv", genres: "theater_comedy",
  tubi: "smart_display", crunchyroll: "smart_display", britbox: "smart_display", "acorn-tv": "smart_display",
  // genres (shared by the TV and Movies nav entries)
  action: "bolt", adventure: "explore", animation: "animation", anime: "auto_awesome",
  "award-show": "emoji_events", children: "child_care", comedy: "sentiment_very_satisfied",
  crime: "gavel", diy: "handyman", documentary: "videocam", drama: "theater_comedy",
  espionage: "fingerprint", family: "family_restroom", fantasy: "castle", food: "restaurant",
  "game-show": "quiz", history: "history_edu", horror: "skull", legal: "balance",
  medical: "medical_services", music: "music_note", mystery: "search", nature: "forest",
  news: "newspaper", "panel-show": "groups", reality: "camera_indoor", romance: "favorite",
  "science-fiction": "rocket_launch", scifi: "rocket_launch", sports: "trophy",
  supernatural: "dark_mode", "talk-show": "mic", thriller: "visibility", travel: "luggage",
  variety: "celebration", war: "swords", western: "landscape",
  // general
  "outer-space": "rocket_launch",
};

const DEFAULT_SYMBOL = "sports";

/**
 * Resolves a nav node to its icon: a hosted brand logo where we have one,
 * otherwise a Material Symbol. Categories use their declared `icon`.
 *
 * @param {object} node - A nav-tree node.
 * @param {boolean} isCategory - Whether the node is a top-level category.
 * @returns {{ kind: "logo", url: string, fallback: string } | { kind: "symbol", symbol: string }}
 */
export function resolveNodeIcon(node, isCategory) {
  if (isCategory) return { kind: "symbol", symbol: node.icon || "explore" };

  // An explicit `icon` forces a Material Symbol over any same-slug logo.
  if (node.icon) return { kind: "symbol", symbol: node.icon };

  // Genres never borrow a same-slug brand logo (e.g. the History genre vs.
  // the History Channel network).
  if (node.kind === "genre") return { kind: "symbol", symbol: symbolFor(node) };

  const logoSlug = LOGO_ALIAS[node.tag] ?? node.tag;
  const file = LOGO_FILES[logoSlug];
  if (file) {
    const ext = file.split(".").pop().toLowerCase();
    return {
      kind: "logo",
      url: `${publicImageHost()}/discovery-logos/${logoSlug}.${ext}`,
      fallback: symbolFor(node),
    };
  }
  return { kind: "symbol", symbol: symbolFor(node) };
}

/**
 * Whether a node resolves to an explicitly mapped icon (declared, logo, or
 * symbol) rather than the default fallback. For nav-tree validation tests.
 *
 * @param {object} node - A nav-tree node.
 * @returns {boolean}
 */
export function hasExplicitIcon(node) {
  if (node.icon) return true;
  if (node.kind !== "genre" && LOGO_FILES[LOGO_ALIAS[node.tag] ?? node.tag]) return true;
  return CONFERENCES.has(node.tag) || Boolean(SLUG_SYMBOL[node.tag]);
}

/** The Material Symbol a node falls back to (also the logo-load-error fallback). */
function symbolFor(node) {
  if (CONFERENCES.has(node.tag)) return "shield";
  return SLUG_SYMBOL[node.tag] || DEFAULT_SYMBOL;
}
